// API para chamar rotas admin do backend Express
const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || 'https://api.halo.ia.br';
const ADMIN_TOKEN = '7638b3be4771552dfb7c1b265a9473b78f05e22912fc344d';

/**
 * Buscar todas as empresas (apenas para admins)
 */
export async function getAdminCompanies() {
  try {
    const response = await fetch(`${BACKEND_URL}/admin/companies`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-Admin-Token': ADMIN_TOKEN,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Erro ao buscar empresas');
    }

    const { companies } = await response.json();
    return companies;
  } catch (error) {
    console.error('Erro em getAdminCompanies:', error);
    throw error;
  }
}

/**
 * Buscar analytics do site Halo
 */
export async function getAdminSiteAnalytics(period = '7days', path = null) {
  try {
    const params = new URLSearchParams({ period: String(period || '7days') });
    if (path) params.set('path', String(path));
    const response = await fetch(`${BACKEND_URL}/admin/site-analytics?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-Admin-Token': ADMIN_TOKEN
      }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Erro ao buscar analytics do site');
    }

    const payload = await response.json();
    return payload;
  } catch (error) {
    console.error('Erro em getAdminSiteAnalytics:', error);
    throw error;
  }
}

/**
 * Adicionar créditos manualmente
 */
export async function addAdminCredits(company_id, amount) {
  try {
    const response = await fetch(`${BACKEND_URL}/admin/add-credits`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Admin-Token': ADMIN_TOKEN
      },
      body: JSON.stringify({ company_id, amount })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Erro ao adicionar créditos');
    }

    const { credits } = await response.json();
    return credits;
  } catch (error) {
    console.error('Erro em addAdminCredits:', error);
    throw error;
  }
}

/**
 * Buscar detalhes de uma empresa (apenas para admins)
 */
export async function getAdminCompanyDetails(companyId) {
  try {
    const response = await fetch(`${BACKEND_URL}/admin/company-details`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Admin-Token': ADMIN_TOKEN,
      },
      body: JSON.stringify({ company_id: companyId }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Erro ao buscar detalhes da empresa');
    }

    const { details } = await response.json();
    return details;
  } catch (error) {
    console.error('Erro em getAdminCompanyDetails:', error);
    throw error;
  }
}

/**
 * Buscar dados de uma seção específica (users, agents, connections, etc)
 */
export async function getAdminSectionData(companyId, section) {
  try {
    const response = await fetch(`${BACKEND_URL}/admin/section-data`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Admin-Token': ADMIN_TOKEN,
      },
      body: JSON.stringify({ 
        company_id: companyId,
        section: section 
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || `Erro ao buscar ${section}`);
    }

    const { data } = await response.json();
    return data;
  } catch (error) {
    console.error(`Erro em getAdminSectionData (${section}):`, error);
    throw error;
  }
}

/**
 * Deletar um item (user, agent, connection, contact, conversation)
 */
export async function deleteAdminItem(type, id) {
  try {
    const response = await fetch(`${BACKEND_URL}/admin/delete-item`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'X-Admin-Token': ADMIN_TOKEN,
      },
      body: JSON.stringify({ 
        type: type,
        id: id 
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Erro ao excluir item');
    }

    const { message } = await response.json();
    return message;
  } catch (error) {
    console.error(`Erro em deleteAdminItem (${type}):`, error);
    throw error;
  }
}

/**
 * Atualizar plano da empresa
 */
export async function updateAdminSubscription(company_id, plan_id, status, renewal_date) {
  try {
    const response = await fetch(`${BACKEND_URL}/admin/update-subscription`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-Admin-Token': ADMIN_TOKEN
      },
      body: JSON.stringify({ company_id, plan_id, status, renewal_date })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Erro ao atualizar plano');
    }

    const { message } = await response.json();
    return message;
  } catch (error) {
    console.error('Erro em updateAdminSubscription:', error);
    throw error;
  }
}

/**
 * Buscar planos disponíveis
 */
export async function getAdminPlans() {
  try {
    const response = await fetch(`${BACKEND_URL}/admin/plans`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-Admin-Token': ADMIN_TOKEN
      }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Erro ao buscar planos');
    }

    const { plans } = await response.json();
    return plans;
  } catch (error) {
    console.error('Erro em getAdminPlans:', error);
    throw error;
  }
}

/**
 * Criar empresa completa (usuário + assinatura + créditos)
 */
export async function createAdminCompany(payload) {
  try {
    const response = await fetch(`${BACKEND_URL}/admin/create-company`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Admin-Token': ADMIN_TOKEN
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const contentType = response.headers.get('content-type');
      let errorMessage = 'Erro ao criar empresa';
      
      if (contentType && contentType.includes('application/json')) {
        try {
          const error = await response.json();
          errorMessage = error.error || error.message || errorMessage;
        } catch (e) {
          errorMessage = `Erro ${response.status}: ${response.statusText}`;
        }
      } else {
        errorMessage = `Erro ${response.status}: ${response.statusText}`;
      }
      
      throw new Error(errorMessage);
    }

    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      throw new Error('Resposta inválida do servidor (não é JSON)');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Erro em createAdminCompany:', error);
    throw error;
  }
}

/**
 * Deletar empresa e todos os dados relacionados
 */
export async function deleteAdminCompany(companyId) {
  try {
    const response = await fetch(`${BACKEND_URL}/admin/company/${companyId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'X-Admin-Token': ADMIN_TOKEN
      }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Erro ao deletar empresa');
    }

    const { message } = await response.json();
    return message;
  } catch (error) {
    console.error('Erro em deleteAdminCompany:', error);
    throw error;
  }
}
